import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { BdeoDrawer, BdeoSelectOption } from '@bdeo/webcomponents';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Damage, DamageSeverity, DamageTypology, vehiclePartsIds, DamageOperation, Smartphoto } from '@bdeo-motor/smartphoto-entities';
import { filter, merge, tap, throttleTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartDamageType } from '../../../../entities/types/part-damage.type';
import { SelectorService } from '../../../../services/selector.service';
import { CompanyService } from '../../../../services/company.service';
import { DamageEstimationRequest, DamageEstimationResponse } from '../../../../entities/types/damage-estimation.type';
import { SmartphotoService } from '../../../../services/smartphoto.service';
import { maxSizeValidator, mimetypeValidator } from 'src/app/validators/file-validators';

@Component({
  selector: 'app-smartphoto-damage-estimation-drawer',
  templateUrl: './smartphoto-damage-estimation-drawer.component.html',
  styleUrl: './smartphoto-damage-estimation-drawer.component.scss'
})
export class SmartphotoDamageEstimationDrawerComponent implements OnInit, OnChanges {
  protected static readonly maxFileSize = 20 * 1024 * 1024;
  protected static readonly validMimetypes = <const>['image/jpg', 'image/jpeg', 'image/png', 'image/heic', 'image/heif'];
  public static readonly defaultPartTypologies = <const>{
    detached: ['-'],
    corrosion: ['high', 'mid', 'low'],
    break_absence: ['high'],
    deformation: ['high', 'mid', 'low'],
    scratch: ['mid', 'low', 'superficial'],
    hail: ['high', 'mid', 'low'],
    blowout: ['high'],
    cutting_tear: ['high', 'mid', 'low']
  };
  private readonly destroyRef = inject(DestroyRef);
  private readonly companyService = inject(CompanyService);
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly selectorService = inject(SelectorService);
  private readonly i18NextService = inject(I18NEXT_SERVICE);
  @ViewChild('drawer') private drawer!: { nativeElement: BdeoDrawer };
  @ViewChild('addImageInput') private addImageInput!: ElementRef<HTMLInputElement>;
  public currency?: string;
  public smartphoto?: Smartphoto;
  @Input() alreadyAddedParts!: string[];
  @Input() damage?: Damage;
  @Input() customPartsSelector?: string;
  @Output() damageSaved = new EventEmitter<Damage>();
  @Output('close') onClose = new EventEmitter();
  public partDamageTypes: Record<string, PartDamageType> = {};
  public availableParts: string[] = [];
  public partSelectOptions: BdeoSelectOption[] = [];
  public typologiesSelectOptions: BdeoSelectOption[] = [];
  public severitiesSelectOptions: BdeoSelectOption[] = [];
  public operations: BdeoSelectOption[] = [
    {
      value: 'replace',
      text: 'replace',
      disabled: false,
      selected: false
    },
    {
      value: 'repair',
      text: 'repair',
      disabled: false,
      selected: false
    }
  ];
  public part = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  public typology = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  public severity = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  public surface = new FormControl<number>({ value: 0, disabled: true }, { nonNullable: true, validators: [Validators.min(0)] });
  public operation = new FormControl<string>({ value: '', disabled: true }, { nonNullable: true, validators: [Validators.required] });
  public partReference = new FormControl<string>({ value: '', disabled: false }, { nonNullable: true });
  public price = new FormControl<number>({ value: 0, disabled: false }, { nonNullable: true, validators: [Validators.min(0)] });
  public paintHours = new FormControl<number>({ value: 0, disabled: false }, { nonNullable: true, validators: [Validators.min(0)] });
  public paintMaterials = new FormControl<number>({ value: 0, disabled: false }, { nonNullable: true, validators: [Validators.min(0)] });
  public labourHours = new FormControl<number>({ value: 0, disabled: false }, { nonNullable: true, validators: [Validators.min(0)] });
  public assemblyHours = new FormControl<number>({ value: 0, disabled: false }, { nonNullable: true, validators: [Validators.min(0)] });
  public damageForm = new FormGroup({
    part: this.part,
    typology: this.typology,
    severity: this.severity,
    surface: this.surface,
    operation: this.operation,
    partReference: this.partReference,
    price: this.price,
    paintHours: this.paintHours,
    paintMaterials: this.paintMaterials,
    labourHours: this.labourHours,
    assemblyHours: this.assemblyHours,
    images: new FormArray<FormControl<{ file: File; fileUrl: string }>>([])
  });
  public saveButtonClicked = false;
  public loading = false;
  private readonly smartphotoChanged$ = this.smartphotoService.getCurrentSmartphoto().pipe(
    filter((s): s is Smartphoto => Boolean(s)),
    tap((smartphoto) => {
      this.smartphoto = smartphoto;
      this.currency = smartphoto.estimationConfig.currency;
      this.updateParts();
      this.resetForm();
    })
  );

  public get images(): FormArray<FormControl<{ file: File; fileUrl: string }>> {
    return this.damageForm.get('images') as FormArray<FormControl<{ file: File; fileUrl: string }>>;
  }

  constructor() {
    this.smartphotoChanged$.pipe().subscribe();
  }

  async ngOnInit(): Promise<void> {
    this.selectPartBehaviour();
    this.selectTypologyBehaviour();
    this.selectSeverityBehaviour();
    this.selectOperationBehaviour();
    this.partDamageTypes = await this.selectorService.get<PartDamageType>('part_damages_types');
    if (this.customPartsSelector) {
      this.partDamageTypes = {
        ...this.partDamageTypes,
        ...(await this.selectorService.get<PartDamageType>(this.customPartsSelector))
      };
    }
    this.updateParts();
    this.operations.forEach((option) => {
      option.text = this.i18NextService.t('brokers:operation.' + option.value) ?? '-';
    });

    this.handleDamageEstimationChange();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['damage']) {
      if (this.damage) {
        this.part.disable();
      } else {
        this.part.enable();
      }

      this.updateParts();
      this.part.setValue(this.damage?.description ?? '');
      this.typology.setValue(this.damage?.typology ?? '');
      this.severity.setValue(this.damage?.severity ?? '');
      this.surface.setValue(this.damage?.area ?? 0);
      this.operation.setValue(this.damage?.estimation.operation ?? '');
      this.labourHours.setValue(this.damage?.estimation.labour.time ?? 0);
      this.paintMaterials.setValue(this.damage?.estimation.paint.materials ?? 0);
      this.paintHours.setValue(this.damage?.estimation.paint.time ?? 0);
      this.assemblyHours.setValue(this.damage?.estimation.assembly.time ?? 0);
      this.price.setValue(this.damage?.estimation.piece ?? 0);
      this.partReference.setValue(this.damage?.estimation.reference ?? '');

      this.damageForm.markAsPristine();
    }
  }

  public updateParts(): void {
    this.availableParts = this.getAvailableParts();
    this.partSelectOptions = this.getPartSelectOptions();
  }

  public selectPartBehaviour(): void {
    this.damageForm
      .get('part')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newPartValue) => {
        if (newPartValue !== '') {
          this.typology.enable();
          this.typology.setValue('');
          this.severity.setValue('');
          this.severity.disable();
          this.surface.enable();
          this.surface.setValue(0);
          this.operation.enable();
          this.operation.setValue('');
          this.operation.disable();
          this.partReference.setValue('');
          this.price.setValue(0);
          this.paintHours.setValue(0);
          this.paintMaterials.setValue(0);
          this.labourHours.setValue(0);
          this.assemblyHours.setValue(0);
          this.typologiesSelectOptions = this.getTypologiesSelectOptions(newPartValue);

          if (this.typologiesSelectOptions.length === 1) {
            this.typology.setValue(this.typologiesSelectOptions[0].value as string);
            this.typology.markAsDirty();
          }
        }
      });
  }

  public selectTypologyBehaviour(): void {
    this.damageForm
      .get('typology')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newTypologyValue) => {
        if (newTypologyValue !== '') {
          this.severity.setValue('');
          this.severity.enable();
          const part = this.damageForm.get('part')?.getRawValue();
          this.severitiesSelectOptions = this.getSeveritiesSelectOptions(part, newTypologyValue);

          if (this.severitiesSelectOptions.length === 1) {
            this.severity.setValue(this.severitiesSelectOptions[0].value as string);
            this.severity.markAsDirty();
          }
        }
      });
  }

  public selectSeverityBehaviour(): void {
    this.damageForm
      .get('severity')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newSeverityValue) => {
        if (newSeverityValue !== '') {
          this.operation.enable();
        }
      });
  }

  public selectOperationBehaviour(): void {
    this.damageForm
      .get('operation')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.partReference.setValue('');
        this.price.setValue(0);
      });
  }

  public resetForm(): void {
    this.part.setValue('');
    this.typology.disable();
    this.typology.setValue('');
    this.severity.setValue('');
    this.severity.disable();
    this.surface.setValue(0);
    this.surface.disable();
    this.operation.setValue('');
    this.operation.disable();
    this.partReference.setValue('');
    this.price.setValue(0);
    this.paintHours.setValue(0);
    this.paintMaterials.setValue(0);
    this.labourHours.setValue(0);
    this.assemblyHours.setValue(0);

    this.images.clear();
  }

  public isRequired(formControl: FormControl): boolean {
    return formControl.hasValidator(Validators.required);
  }

  public getAvailableParts(): string[] {
    if (this.damage) {
      return [this.damage?.description];
    }

    return Object.entries(this.partDamageTypes)
      .filter(([name]) => !this.alreadyAddedParts.includes(name) && name !== 'custom')
      .map(([name]) => name);
  }

  public getPartSelectOptions(): BdeoSelectOption[] {
    return this.availableParts
      .map((name) => {
        if (!this.i18NextService.exists(`brokers:parts.${name}`)) {
          return {
            name: `* ${name}`,
            value: name,
            text: `${name}`,
            disabled: false,
            selected: false
          };
        }

        const translated = this.i18NextService.t(`brokers:parts.${name}`) ?? '-';
        return {
          name: translated,
          value: name,
          text: translated,
          disabled: false,
          selected: false
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  public getTypologiesSelectOptions(partDescription: string): BdeoSelectOption[] {
    const realTKeys = this.partDamageTypes[partDescription] ?? { 'N/A': ['-'] };
    const typologiesKeys =
      this.partDamageTypes[partDescription] === undefined ? SmartphotoDamageEstimationDrawerComponent.defaultPartTypologies : realTKeys;

    return Object.keys(typologiesKeys)
      .map((typology) => {
        const translated = this.i18NextService.t(`brokers:typologies.${typology}`) ?? '-';
        return {
          name: translated,
          value: typology,
          text: translated,
          disabled: false,
          selected: false
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  public getSeveritiesSelectOptions(partDescription: string, damageTypology: string): BdeoSelectOption[] {
    const realTKeys = this.partDamageTypes[partDescription] ?? { 'N/A': ['-'] };
    const typologiesKeys =
      this.partDamageTypes[partDescription] === undefined ? SmartphotoDamageEstimationDrawerComponent.defaultPartTypologies : realTKeys;

    return (typologiesKeys[damageTypology as keyof typeof typologiesKeys] ?? []).map((severity) => {
      const translated = this.i18NextService.t(`brokers:gravity.${severity}`) ?? '-';
      return {
        name: translated,
        value: severity,
        text: translated,
        disabled: false,
        selected: false
      };
    });
  }

  public increment(formControl: FormControl<number | null>, incrementAmount = 1): void {
    if (formControl) {
      formControl.setValue(Math.round((Number(formControl.value ?? 0) + incrementAmount) * 100) / 100);
    }
    formControl.markAsDirty();
  }

  public decrement(formControl: FormControl<number | null>, decrementAmount = 1): void {
    if (!formControl.value || formControl.value - decrementAmount < 0) {
      formControl.setValue(0);
    } else {
      formControl.setValue(Math.round((Number(formControl.value) - decrementAmount) * 100) / 100);
    }
    formControl.markAsDirty();
  }

  public open(): void {
    if (!this.drawer) {
      return;
    }

    this.saveButtonClicked = false;
    this.drawer.nativeElement.show = true;
    this.damageForm.markAsPristine();
  }

  public close(): void {
    this.drawer.nativeElement.show = false;
    this.resetForm();
  }

  public save(): void {
    this.saveButtonClicked = true;

    if (!this.damageForm.valid) {
      return;
    }

    const images = this.images.controls.map((image) => image.value.file);

    if (this.damage) {
      this.updateDamage(this.damage);
    } else {
      this.damage = this.createDamage();
    }

    this.smartphotoService.addImagesToCurrentSmartphoto(images, this.damage);
    this.damageSaved.emit(this.damage);

    this.close();
  }

  public openCaptureImageDialog(): void {
    this.addImageInput.nativeElement.click();
  }

  public addImage(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const fileUrl = URL.createObjectURL(file);
    const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';

    const imageControl = new FormControl(
      { file: new File([file], `${file.name.split('.')[0]}.${fileExtension}`, { type: file.type }), fileUrl },
      {
        nonNullable: true,
        validators: [
          mimetypeValidator(SmartphotoDamageEstimationDrawerComponent.validMimetypes),
          maxSizeValidator(SmartphotoDamageEstimationDrawerComponent.maxFileSize)
        ]
      }
    );

    this.images.push(imageControl);
  }

  public removeImage(index: number): void {
    this.images.removeAt(index);
    this.addImageInput.nativeElement.value = '';
  }

  protected createDamage(): Damage {
    const damage = new Damage({
      damages: [
        {
          area: 0,
          is_deleted: false,
          origin: 'claim',
          severity: '-',
          typology: 'scratch',
          is_extra: true,
          uuid: crypto.randomUUID()
        }
      ],
      part: {
        description: '',
        id: '',
        zone: null
      },
      estimation: {}
    });

    damage.estimation.updatePrice({
      labour: this.smartphoto?.estimationConfig.labourHourPrice ?? 0,
      paint: this.smartphoto?.estimationConfig.paintHourPrice ?? 0
    });

    this.updateDamage(damage);

    return damage;
  }

  protected updateDamage(damage: Damage): void {
    damage.area = Number(this.surface.value);
    damage.severity = this.severity.value as DamageSeverity;
    damage.typology = this.typology.value as DamageTypology;

    damage.description = this.part.value;
    damage.estimation.update({
      strip_assembly: Number(this.assemblyHours.value),
      labour_time: Number(this.labourHours.value),
      operation: this.operation.value as DamageOperation,
      paint_materials: Number(this.paintMaterials.value),
      paint_time: Number(this.paintHours.value),
      part_price: Number(this.price.value),
      part_reference: this.partReference.value as string,
      part_provider: undefined
    });

    if (this.isCustomPart(damage.description)) {
      damage.partId = 'custom';
    } else {
      damage.partId = vehiclePartsIds[damage.description as keyof typeof vehiclePartsIds]?.id ?? 'custom';
    }

    damage.custom = damage.partId === 'custom';
  }

  private isCustomUserPart(): boolean {
    return !this.availableParts.find((part) => this.damageForm.get('part')?.value === part);
  }

  private isCustomPart(description: string): boolean {
    return !this.partDamageTypes[description];
  }

  private handleDamageEstimationChange(): void {
    merge(this.part.valueChanges, this.typology.valueChanges, this.severity.valueChanges, this.operation.valueChanges)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => !this.loading && !this.isCustomUserPart()),
        throttleTime(250, undefined, { leading: true, trailing: true })
      )
      .subscribe(async () => {
        const damageEstimationTrigger =
          !this.part.pristine &&
          !this.typology.pristine &&
          !this.severity.pristine &&
          !this.operation.pristine &&
          this.paintHours.pristine &&
          this.paintMaterials.pristine &&
          this.labourHours.pristine &&
          this.assemblyHours.pristine;

        if (!damageEstimationTrigger) {
          return;
        }

        const { part, typology, severity, operation } = this.damageForm.getRawValue();

        const isIncomplete = [part, typology, severity, operation].some((value) => !value);

        if (isIncomplete) {
          return;
        }

        const { costEstimationProvider, id } = this.companyService.getCurrentCompany() ?? {};
        const { vinDetection } = this.smartphoto?.fraudSummary ?? {};

        if (!id || !this.smartphoto) {
          return;
        }

        const damageRequest: DamageEstimationRequest = {
          company_id: id,
          smartphoto_id: this.smartphoto.id,
          cost_estimation_provider: costEstimationProvider ?? { name: 'inhouse-default-spain' },
          vehicle_information: {
            body_type: vinDetection?.detail?.decoded_body_type ?? 'HATCHBACK'
          },
          cost_estimation_info: {
            paint_system: this.smartphoto.paintSystem
          },
          detected_parts_per_image: this.smartphoto.detectedPartsPerImage ?? {},
          damages: [
            {
              part_id: this.isCustomPart(part) ? 'custom' : (part as string).toLowerCase().replace(/ /g, '_'),
              part_description: part,
              typology: typology,
              typology_user: typology,
              severity: severity,
              severity_user: severity,
              operation: operation,
              operation_user: operation,
              uuid: ''
            }
          ]
        };

        let result: DamageEstimationResponse = {};

        try {
          this.loading = true;
          result = await this.smartphotoService.getDamageEstimation(damageRequest);

          this.damageForm.patchValue({
            price: result.part_price ?? 0,
            labourHours: result.labour_time ?? 0,
            assemblyHours: result.strip_assembly_time ?? 0,
            paintHours: result.paint_time ?? 0,
            paintMaterials: result.paint_materials ?? 0
          });
        } finally {
          this.loading = false;
        }
      });
  }
}
